<template>
  <div>
    <div class="actions row col-lg-12 mb-5 p-0 m-0 justify-content-end">
      <div class="actions-right row col-lg-6 row justify-content-end p-0 m-0">
        <button type="button" class="btn btn-primary" @click="openAddModal">{{ $t('add') }}</button>
      </div>
    </div>
    <CTableWrapper 
      ref="table" 
      hover 
      striped 
      :caption="$t('satisfactionQuestions')" 
      :items="items" 
      :actions="actions" 
      :pages="pages" 
      :fields="fields" 
      clickable-rows 
      @row-clicked="rowClicked" 
      @action-clicked="actionClicked" 
      @update-requested="updateItemsRequested" 
      :sorter="{external: true}" 
      :sorterValue="sorterValue" 
      @sorter-value-changed="sorterValueChanged" 
      :loading="loading">
    </CTableWrapper>
    
    <!-- Modal para criar/editar pergunta -->
    <CModal
      :show.sync="showModal"
      :title="editingQuestion ? $t('edit') : $t('add')"
      color="primary"
      size="lg"
    >
      <CForm>
        <div role="group" class="form-group d-flex justify-content-end mb-3">
          <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
        </div>
        
        <div role="group" class="form-group">
          <label>{{ $t('question') }} *</label>
          <CInput 
            type="text" 
            v-model="form.questions[currentLanguage.key]" 
            :invalid-feedback="errorOutput['Questions']"
            :is-valid="errorValidator('Questions')"
            :placeholder="currentLanguage.key === 'pt' ? 'Digite a pergunta em português' : 'Type the question in English'"
          />
        </div>
        
        <div role="group" class="form-group">
          <label>{{ $t('type') }} *</label>
          <select 
            v-model="form.type" 
            class="form-control" 
            :class="{'is-invalid': errorValidator('Type') === false}"
            @blur="validateField('Type')"
          >
            <option :value="null">{{ $t('selectType') }}</option>
            <option value="Rating">{{ $t('rating') }}</option>
            <option value="OpenEnded">{{ $t('openEnded') }}</option>
          </select>
          <div v-if="errorOutput['Type']" class="invalid-feedback">{{ errorOutput['Type'] }}</div>
        </div>
        
        <div role="group" class="form-group">
          <label>{{ $t('position') }}</label>
          <CInput 
            type="number" 
            v-model.number="form.position" 
            :invalid-feedback="errorOutput['Position']"
            :is-valid="errorValidator('Position')"
            placeholder="0"
            @blur="validateField('Position')"
          />
        </div>
      </CForm>
      
      <template #footer>
        <CButton @click="showModal = false" color="secondary">{{ $t('cancel') }}</CButton>
        <CButton @click="saveQuestion" color="primary" :disabled="saving">
          <CSpinner v-if="saving" size="sm" class="mr-1"></CSpinner>
          {{ $t('save') }}
        </CButton>
      </template>
    </CModal>
    
    <!-- Modal de confirmação de exclusão -->
    <CModal
      :show.sync="showDeleteModal"
      :title="$t('confirmDelete')"
      color="primary"
    >
      <p>{{ $t('confirmRemoveQuestion', { question: questionToDelete && questionToDelete.question ? questionToDelete.question : '' }) }}</p>
      <template #footer>
        <CButton @click="showDeleteModal = false" color="secondary">{{ $t('cancel') }}</CButton>
        <CButton @click="confirmDelete" color="primary" :disabled="deleting">
          <CSpinner v-if="deleting" size="sm" class="mr-1"></CSpinner>
          {{ $t('delete') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import CTableWrapper from "@/controls/PaginatedTable.vue";
import InputGetList from "@/classes/InputGetList";
import i18n from '@/helpers/i18n';
import { convertToPascalCase } from "@/helpers/utils.js";
import LanguageDropdown from "@/controls/LanguageDropdown.vue";
import { CONSTANTS } from '@/helpers/consts';

export default {
  components: { CTableWrapper, LanguageDropdown },
  data () {
    return {
      itemsPerPage: localStorage.itemsPerPage ?? 10,
      fields: [
        { key: 'question', label: i18n.t('question'), _classes: 'font-weight-bold', sortable: true },
        { key: 'typeDisplay', label: i18n.t('type'), sortable: true },
        { key: 'position', label: i18n.t('position'), sortable: true },
      ],
      actions: [
        { key: 'edit', label: i18n.t('edit') },
        { key: 'remove', label: i18n.t('remove') }
      ],
      sorterValue: { column: 'position', asc: true },
      loading: false,
      showModal: false,
      showDeleteModal: false,
      editingQuestion: null,
      questionToDelete: null,
      saving: false,
      deleting: false,
      triedSubmit: false,
      touchedFields: {},
      form: {
        questions: {
          pt: '',
          en: ''
        },
        type: null,
        position: 0
      },
      currentLanguage: CONSTANTS.supportedLanguages[0]
    }
  },
  computed: {
    ...mapState('satisfactionQuestions', ['getSatisfactionQuestionsOutput']),
    ...mapState('satisfactionQuestions', ['deleteSatisfactionQuestionByIdOutput']),
    ...mapState('satisfactionQuestions', ['postSatisfactionQuestionOutput']),
    ...mapState('satisfactionQuestions', ['putSatisfactionQuestionOutput']),
    ...mapState({ 
      alertType: state => state.alert.type,
      alertOutput: state => state.alert.output
    }),
    questionsInfo: function() {
      return this.getSatisfactionQuestionsOutput ? this.getSatisfactionQuestionsOutput : null;
    },
    items: function()
    {
      if (!this.questionsInfo || !this.questionsInfo.data) return [];
      
      return this.questionsInfo.data.map(item => ({
        ...item,
        typeDisplay: this.getTypeLabel(item.type)
      }));
    },
    pages: function()
    {
      return this.questionsInfo ? this.questionsInfo.totalPages : 1;
    },
    errorOutput: function() {
      var result = {};
      if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
      {
        result = this.alertOutput.reduce(function(accum, currentVal) {
          accum[currentVal.key] = currentVal.value;
          return accum;
        }, {});
      }

      // Validate multilanguage fields only when user tried to submit or field was touched
      if (this.triedSubmit || this.touchedFields['Questions']) {
        for (const [key, value] of Object.entries(this.form.questions)) { 
          if(!value || !value.trim()) { 
            result['Questions'] = this.$i18n.t("validateBothTranslations"); 
            break; 
          }; 
        }
      }

      return result;
    }
  },
  mounted: function()
  {
    this.updateItemsRequested(1, this.itemsPerPage);
  },
  methods: {
    ...mapActions('satisfactionQuestions', ['getSatisfactionQuestions']),
    ...mapActions('satisfactionQuestions', ['deleteSatisfactionQuestionById']),
    ...mapActions('satisfactionQuestions', ['postSatisfactionQuestion']),
    ...mapActions('satisfactionQuestions', ['putSatisfactionQuestion']),
    getTypeLabel(type) {
      if (type === 'Rating' || type === 0 || type === '0') return this.$t('rating');
      if (type === 'OpenEnded' || type === 1 || type === '1') return this.$t('openEnded');
      return type;
    },
    errorValidator(field) {
      // Only show validation state if user tried to submit or field was touched
      if (!this.triedSubmit && !this.touchedFields[field]) {
        return null;
      }
      return !this.errorOutput[field];
    },
    validateField(field) {
      this.touchedFields[field] = true;
      
      if (field === 'Questions') {
        // Validate both translations
        let hasError = false;
        if (!this.form.questions.pt || !this.form.questions.pt.trim()) {
          this.errorOutput['Questions'] = this.$i18n.t("validateBothTranslations");
          hasError = true;
        } else if (!this.form.questions.en || !this.form.questions.en.trim()) {
          this.errorOutput['Questions'] = this.$i18n.t("validateBothTranslations");
          hasError = true;
        }
        if (!hasError) {
          delete this.errorOutput['Questions'];
        }
      } else if (field === 'Type') {
        if (!this.form.type) {
          this.errorOutput['Type'] = this.$t('validation.required') || 'Required';
        } else {
          delete this.errorOutput['Type'];
        }
      } else if (field === 'Position') {
        // Position is optional, so no validation needed
        delete this.errorOutput['Position'];
      }
    },
    rowClicked (item, index) {
      this.openEditModal(item);
    },
    actionClicked(item, action){
      switch(action.key)
      {
        case 'edit':
        {
          this.openEditModal(item);
          break;
        }
        case 'remove':
        {
          this.openDeleteModal(item);
          break;
        }
        default:
        {
          throw "The provided action is not supported!"; 
        }
      }
    },
    openAddModal() {
      this.editingQuestion = null;
      this.triedSubmit = false;
      this.touchedFields = {};
      this.form = {
        questions: {
          pt: '',
          en: ''
        },
        type: null,
        position: 0
      };
      this.showModal = true;
    },
    openEditModal(item) {
      // Find original item from questionsInfo to preserve all data
      const originalItem = this.questionsInfo && this.questionsInfo.data 
        ? this.questionsInfo.data.find(q => q.id === item.id) 
        : item;
      
      this.editingQuestion = originalItem;
      this.triedSubmit = false;
      this.touchedFields = {};
      
      let typeValue = null;
      if (originalItem.type === 0 || originalItem.type === 'Rating' || originalItem.type === '0') {
        typeValue = 'Rating';
      } else if (originalItem.type === 1 || originalItem.type === 'OpenEnded' || originalItem.type === '1') {
        typeValue = 'OpenEnded';
      } else {
        typeValue = originalItem.type;
      }
      
      // For editing, use translations from backend if available
      const questionsDict = originalItem.questions || {};
      this.form = {
        questions: {
          pt: questionsDict.pt || questionsDict['pt'] || originalItem.question || '',
          en: questionsDict.en || questionsDict['en'] || originalItem.question || ''
        },
        type: typeValue,
        position: originalItem.position
      };
      this.showModal = true;
    },
    openDeleteModal(item) {
      this.questionToDelete = item;
      this.showDeleteModal = true;
    },
    saveQuestion() {
      this.triedSubmit = true;
      // Mark all required fields as touched when user tries to save
      this.touchedFields['Questions'] = true;
      this.touchedFields['Type'] = true;
      
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      
      // Use Portuguese (1) as default language, or use the language from editing if available
      const defaultLanguage = this.editingQuestion && this.editingQuestion.language 
        ? this.editingQuestion.language 
        : 1; // pt = 1, en = 2
      
      const payload = {
        questions: this.form.questions,
        language: defaultLanguage,
        type: this.form.type,
        position: this.form.position || 0
      };
      
      if (this.editingQuestion) {
        this.putSatisfactionQuestion({ id: this.editingQuestion.id, input: payload });
      } else {
        this.postSatisfactionQuestion(payload);
      }
    },
    validateForm() {
      if (!this.form.questions.pt || !this.form.questions.pt.trim()) {
        return false;
      }
      if (!this.form.questions.en || !this.form.questions.en.trim()) {
        return false;
      }
      if (!this.form.type) {
        return false;
      }
      return true;
    },
    confirmDelete() {
      if (!this.questionToDelete) return;
      
      this.deleting = true;
      this.deleteSatisfactionQuestionById(this.questionToDelete.id);
    },
    updateItemsRequested(currentPage, itemsPerPage)
    {
      this.loading = true;
      this.itemsPerPage = itemsPerPage;

      var queryParams = {
        pageNumber: currentPage,
        pageSize: itemsPerPage
      };
      
      this.getSatisfactionQuestions(queryParams); 
    },
    sorterValueChanged(value)
    {
      this.sorterValue = value;
    }    
  },
  watch:
  {
    sorterValue:
    {
      handler: function(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      },
      deep: true
    },
    deleteSatisfactionQuestionByIdOutput: function(val)
    {
      this.loading = true;
      this.deleting = false;
      this.showDeleteModal = false;
      this.questionToDelete = null;

      if(val)
      {
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    postSatisfactionQuestionOutput: function(val)
    {
      this.saving = false;
      if(val)
      {
        this.showModal = false;
        this.triedSubmit = false;
        this.touchedFields = {};
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    putSatisfactionQuestionOutput: function(val)
    {
      this.saving = false;
      if(val)
      {
        this.showModal = false;
        this.triedSubmit = false;
        this.touchedFields = {};
        this.updateItemsRequested(1, this.itemsPerPage);
      }
    },
    showModal: function(val) {
      // Reset validation state when modal closes
      if (!val) {
        this.triedSubmit = false;
        this.touchedFields = {};
      }
    },
    getSatisfactionQuestionsOutput: function(val)
    {
      if(val != null)
      {
        this.loading = false;
      }
    },
    alertType: function() {
      this.saving = false;
      this.deleting = false;
    }
  }
}
</script>
 
<style>
th
{
  border-top: none !important;
}

tr
{
  cursor: pointer;
}

.status-button
{
  border: none;
  padding: 0;
  text-decoration: underline;
  color: #321fdb;
}
</style>

